import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { counselorSlots } from "../data/content";
import { createBookingAndLead } from "../lib/storage";

export default function BookingForm({
  tutor,
  defaultTarget = "",
  defaultSlot = "",
  submitLabel = "Schedule Free Demo",
  compact = false,
  contextLabel = "landing",
  showPayment = false,
  onPay,
  variant = "light",
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    studentName: "",
    mobile: "",
    targetExam: defaultTarget,
    preferredSlot: defaultSlot,
  });
  const [status, setStatus] = useState({ type: "idle", message: "" });

  const slotOptions = useMemo(
    () => (tutor?.availabilitySlots?.length ? tutor.availabilitySlots : counselorSlots),
    [tutor],
  );

  useEffect(() => {
    setForm((current) => ({
      ...current,
      targetExam: defaultTarget,
      preferredSlot: defaultSlot || slotOptions[0] || "",
    }));
  }, [defaultSlot, defaultTarget, slotOptions]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const cleanMobile = form.mobile.replace(/\D/g, "").slice(-10);
    if (!form.studentName.trim()) {
      setStatus({ type: "error", message: "Please enter the student name." });
      return;
    }
    if (cleanMobile.length !== 10) {
      setStatus({ type: "error", message: "Please enter a valid 10-digit mobile number." });
      return;
    }
    if (!form.targetExam.trim()) {
      setStatus({ type: "error", message: "Please enter the target exam or class." });
      return;
    }
    if (!form.preferredSlot.trim()) {
      setStatus({ type: "error", message: "Please choose a preferred slot." });
      return;
    }

    const tutorName = tutor?.name || "Academic Counselor";
    const result = createBookingAndLead({
      tutorId: tutor?.id || "academic-counselor",
      tutorName,
      studentName: form.studentName.trim(),
      mobile: cleanMobile,
      targetExam: form.targetExam.trim(),
      preferredSlot: form.preferredSlot.trim(),
      source: contextLabel,
    });

    window.open(result.whatsappUrl, "_blank", "noopener,noreferrer");
    setStatus({
      type: "success",
      message: "Booking saved. WhatsApp should open in a new tab for confirmation.",
    });

    navigate("/booking-success", {
      state: { booking: result.booking },
    });
  };

  const panelClass = compact
    ? "space-y-4"
    : variant === "dark"
      ? "dark-shell space-y-4 p-6 sm:p-7"
      : "surface-panel space-y-4 p-6 sm:p-7";

  const copyTone = variant === "dark" ? "text-white/74" : "text-slate-600";
  const labelTone = variant === "dark" ? "text-white/90" : "text-slate-700";
  const fieldClass = variant === "dark" ? "form-field-dark" : "form-field";

  return (
    <form
      onSubmit={handleSubmit}
      className={panelClass}
      aria-label={`Booking form for ${tutor?.name || "Dreameer Education"}`}
    >
      {!compact ? (
        <div className="space-y-2">
          <div className={variant === "dark" ? "eyebrow" : "eyebrow-light"}>Free Demo</div>
          <h3 className={`font-display text-2xl font-extrabold ${variant === "dark" ? "text-white" : "text-ink"}`}>
            Book a Free Demo
          </h3>
          <p className={`text-base leading-7 ${copyTone}`}>
            Get a callback from our academic counselor within 24 hours to schedule your
            diagnostic.
          </p>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <label className={`space-y-2 text-base font-semibold ${labelTone}`}>
          <span>Student Name</span>
          <input
            type="text"
            name="studentName"
            value={form.studentName}
            onChange={handleChange}
            className={fieldClass}
            placeholder="Student Name"
            aria-required="true"
          />
        </label>
        <label className={`space-y-2 text-base font-semibold ${labelTone}`}>
          <span>Mobile Number (+91)</span>
          <input
            type="tel"
            name="mobile"
            value={form.mobile}
            onChange={handleChange}
            className={fieldClass}
            placeholder="Mobile Number (+91)"
            aria-required="true"
          />
        </label>
      </div>

      <label className={`space-y-2 text-base font-semibold ${labelTone}`}>
        <span>Target Exam / Class</span>
        <input
          type="text"
          name="targetExam"
          value={form.targetExam}
          onChange={handleChange}
          className={fieldClass}
          placeholder="Target Exam / Class"
          aria-required="true"
        />
      </label>

      <label className={`space-y-2 text-base font-semibold ${labelTone}`}>
        <span>Preferred Slot</span>
        <select
          name="preferredSlot"
          value={form.preferredSlot}
          onChange={handleChange}
          className={fieldClass}
          aria-required="true"
        >
          {slotOptions.map((slot) => (
            <option key={slot} value={slot}>
              {slot}
            </option>
          ))}
        </select>
      </label>

      {status.message ? (
        <div
          className={`rounded-2xl px-4 py-3 text-sm ${
            status.type === "error"
              ? "bg-coral-100 text-rose-700"
              : variant === "dark"
                ? "bg-white/10 text-emerald-200"
                : "bg-emerald-50 text-emerald-700"
          }`}
          role="status"
        >
          {status.message}
        </div>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row">
        <button type="submit" className="button-primary flex-1">
          {submitLabel}
        </button>
        {showPayment ? (
          <button type="button" className="button-secondary flex-1" onClick={onPay}>
            Pay
          </button>
        ) : null}
      </div>

      <a
        href="https://wa.me/919507505612"
        target="_blank"
        rel="noreferrer"
        className={`inline-flex text-sm font-semibold ${
          variant === "dark" ? "text-skyglow hover:text-white" : "text-emerald-700 hover:text-emerald-800"
        }`}
      >
        Or chat directly with us on WhatsApp
      </a>
    </form>
  );
}
